// Quests — daily + weekly with progress bars + claim
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Trophy, Calendar, Sparkles, Lock, Gift } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";

export default function Quests() {
  const { user, refresh } = useAuth();
  const [quests, setQuests] = useState([]);
  const [badges, setBadges] = useState([]);
  const [drops, setDrops] = useState([]);

  const load = async () => {
    if (!user) return;
    const [q, b, d] = await Promise.all([
      api.get("/quests"), api.get("/quests/badges"), api.get("/coins/drops"),
    ]);
    setQuests(q.data); setBadges(b.data); setDrops(d.data);
  };
  useEffect(() => { load(); }, [user]);

  if (!user) return <div className="p-20 text-center"><Lock className="w-10 h-10 mx-auto text-white/40 mb-4" /><p className="text-white/40">Sign in to begin your quests.</p><Link to="/auth" className="btn-nexus inline-flex mt-6">Enter NEXUS</Link></div>;

  const claim = async (q) => {
    try {
      const { data } = await api.post(`/quests/${q.id}/claim`);
      toast.success(`+${data.rewards.xp} XP · +${data.rewards.coins} NX${data.rewards.badge ? ` · 🏅 ${data.rewards.badge}` : ""}`);
      refresh(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Claim failed"); }
  };

  const daily = quests.filter((q) => q.type === "daily");
  const weekly = quests.filter((q) => q.type === "weekly");

  return (
    <div className="max-w-[1500px] mx-auto space-y-12" data-testid="quests-page">
      <Toaster theme="dark" position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// quests · rewards</div>
          <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">Earn your <span className="text-nexus-cyan">badges</span>.</h1>
          <p className="text-white/50 mt-3 max-w-xl">Daily quests reset every 24 hours. Weekly quests reset every Monday. XP is your member-level — infinite, non-monetary, shows your engagement. NX coins are your spendable currency. Badges are forever.</p>
        </div>
        <div className="grid grid-cols-3 gap-px bg-white/5 text-center">
          <div className="bg-nexus-surface p-4"><div className="label-eyebrow">XP</div><div className="font-display font-black text-2xl mt-1">{user.xp}</div></div>
          <div className="bg-nexus-surface p-4"><div className="label-eyebrow">NX</div><div className="font-display font-black text-2xl mt-1 text-nexus-cyan">{(user.wallet_balance || 0).toFixed(0)}</div></div>
          <div className="bg-nexus-surface p-4"><div className="label-eyebrow">Badges</div><div className="font-display font-black text-2xl mt-1 text-nexus-amber">{badges.length}</div></div>
        </div>
      </div>

      {/* COIN DROPS */}
      {drops.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <Gift className="w-5 h-5 text-nexus-amber animate-pulse" />
            <h2 className="font-display text-sm tracking-[0.3em] uppercase">Flash Drops <span className="text-[10px] text-nexus-crimson ml-2">● Live</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {drops.map((d) => (
              <div key={d.id} className="bg-nexus-panel border-l-2 border-nexus-amber p-5">
                <div className="label-eyebrow text-nexus-amber">{d.label}</div>
                <div className="font-display font-black text-3xl mt-2">{d.coins} NX</div>
                <div className="text-sm text-white/60 mt-1">${d.discounted_price.toFixed(2)}</div>
                <div className="text-[10px] text-white/40 tracking-wider mt-2">ends {new Date(d.ends_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* DAILY */}
      <section data-testid="daily-quests" className="space-y-4">
        <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-nexus-cyan" /><h2 className="font-display text-sm tracking-[0.3em] uppercase">Daily Quests</h2></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {daily.map((q) => <QuestCard key={q.id} q={q} onClaim={() => claim(q)} />)}
        </div>
      </section>

      {/* WEEKLY */}
      <section data-testid="weekly-quests" className="space-y-4">
        <div className="flex items-center gap-3"><Trophy className="w-5 h-5 text-nexus-amber" /><h2 className="font-display text-sm tracking-[0.3em] uppercase">Weekly Quests · Badge Rewards</h2></div>
        <div className="grid sm:grid-cols-2 gap-px bg-white/5">
          {weekly.map((q) => <QuestCard key={q.id} q={q} onClaim={() => claim(q)} large />)}
        </div>
      </section>

      {/* BADGES */}
      <section className="space-y-4">
        <div className="flex items-center gap-3"><Sparkles className="w-5 h-5 text-nexus-magenta" /><h2 className="font-display text-sm tracking-[0.3em] uppercase">XP Level Badges</h2></div>
        {badges.length === 0 && <p className="text-white/40 text-sm">No badges yet — complete weekly quests to earn level badges.</p>}
        <div className="flex flex-wrap gap-3">
          {badges.map((b, i) => (
            <div key={i} className="flex items-center gap-2 px-4 py-2 border" style={{ borderColor: b.color, color: b.color }}>
              <span className="text-lg" style={{ filter: `drop-shadow(0 0 8px ${b.color})` }}>{b.icon}</span>
              <span className="font-display text-[10px] tracking-[0.3em] uppercase">{b.label}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function QuestCard({ q, onClaim, large }) {
  const pct = Math.min(100, (q.progress / q.goal) * 100);
  return (
    <div className={`bg-nexus-surface p-5 group ${large ? "lg:p-7" : ""}`} data-testid={`quest-${q.id}`} style={q.completed && !q.claimed ? { borderLeft: `2px solid ${q.color}` } : {}}>
      <div className="flex items-start gap-4">
        <div className="text-3xl" style={{ filter: `drop-shadow(0 0 10px ${q.color})` }}>{q.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-display font-bold text-base">{q.name}</div>
            {q.badge_label && <span className="text-[9px] tracking-[0.3em] uppercase font-display" style={{ color: q.color }}>★ {q.badge_label}</span>}
          </div>
          <div className="text-xs text-white/50 mt-1">{q.tagline}</div>
          <div className="mt-3 flex items-center gap-2 text-[11px] tracking-wider font-mono">
            <span style={{ color: q.color }}>{q.progress}</span>
            <span className="text-white/30">/ {q.goal}</span>
          </div>
          <div className="mt-2 h-1 bg-white/5 overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: q.color, boxShadow: `0 0 12px ${q.color}` }} />
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-display">+{q.reward_xp} XP · +{q.reward_coins} NX</div>
            {q.claimed
              ? <div className="text-[10px] tracking-[0.3em] uppercase text-nexus-mint font-display">● Claimed</div>
              : q.completed
                ? <button onClick={onClaim} className="btn-nexus !py-1.5 !px-3 text-[9px]" data-testid={`claim-${q.id}`}>Claim</button>
                : <div className="text-[10px] text-white/30 tracking-wider">in progress</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
